import React from 'react'
import parse from 'html-react-parser'
import Button from '@veneer/core/dist/esm/scripts/button'
import LottieAnimation from '../pages/Instructions/LottieAnimation'
import { resolveReplacerTag } from './resolveReplacerTag'
import { iconMapper } from './iconMapper'
import { pascalToSnake } from './stringUtils'

/**
 * Comprehensive HTML parser that handles multiple ContentStack elements:
 * - Icons: <span>[[ IconName ]]</span>
 * - Buttons: <button>[[ ButtonName ]]</button>
 * - Lottie animations: <span data-lottie="url" data-lottie-index="index"></span>
 * - Replacer tags: <element data-replacer="key"></element>
 *
 * @param {Object} params
 * @param {string} params.htmlString - The HTML string to parse
 * @param {Array<{button: string, props: object}>} [params.buttonsToParse=[]] - Array of button configurations
 * @param {Object.<string, React.ReactNode|React.ComponentType>} [params.replacements={}] - Replacer tag mappings
 * @returns {React.ReactNode|null} The parsed React nodes or null if input is invalid
 */
const renderContentStackInHtml = ({
  htmlString,
  buttonsToParse = [],
  replacements = {}
}) => {
  if (!htmlString) return null

  const replacementMap = replacements || {}

  // NOTE: The extra escape characters are used to avoid issues with regex parsing
  // eslint-disable-next-line no-useless-escape
  const buttonNameRegex = /\[\[\s([^\]\[]+)\s\]\]/

  // Create button map from buttonsToParse
  const buttonMap = buttonsToParse.reduce((acc, { button, props }) => {
    const match = button.match(buttonNameRegex)
    if (match && match[1]) {
      acc[match[1]] = props
    }
    return acc
  }, {})

  const parserOptions = {
    replace: (domNode) => {
      const className =
        domNode.attribs?.['class'] || domNode.attribs?.['className'] || ''

      // Handle Icons in spans with [[ IconName ]] format
      if (
        domNode.type === 'tag' &&
        domNode.name === 'span' &&
        domNode.children &&
        domNode.children.length === 1 &&
        domNode.children[0].type === 'text'
      ) {
        const text = domNode.children[0].data.trim()
        const match = text.match(/^\[\[\s*([\w]+)\s*\]\]$/)
        if (match) {
          const iconKey = pascalToSnake(match[1])
          if (iconMapper[iconKey]) {
            return iconMapper[iconKey] // Return directly without cloneElement
          }
        }
      }

      // Handle Buttons with [[ ButtonName ]] format
      if (
        domNode.type === 'tag' &&
        domNode.name === 'button' &&
        domNode.children &&
        domNode.children.length === 1 &&
        domNode.children[0].type === 'text'
      ) {
        const match = domNode.children[0].data.match(buttonNameRegex)
        if (match && match[1] && buttonMap[match[1]]) {
          const buttonName = match[1]
          const props = buttonMap[buttonName] || {}
          return (
            <Button key={buttonName} {...props}>
              {props.buttonName || buttonName}
            </Button>
          )
        }
      }

      // Handle Lottie animations with data-lottie attribute
      if (
        domNode.type === 'tag' &&
        domNode.name === 'span' &&
        domNode?.attribs?.['data-lottie']
      ) {
        const lottie = domNode.attribs['data-lottie']
        const lottieIndex = domNode.attribs?.['data-lottie-index']

        return (
          <LottieAnimation
            url={lottie}
            index={lottieIndex}
            className={className}
            key={`lottie-${lottieIndex || 'default'}`}
          />
        )
      }

      // Handle Replacer tags with data-replacer attribute
      if (domNode.type === 'tag' && domNode?.attribs?.['data-replacer']) {
        const replacedNode = resolveReplacerTag({
          domNode,
          parserOptions,
          replacements: replacementMap
        })

        if (replacedNode !== undefined) {
          return replacedNode
        }
      }

      return undefined
    }
  }

  return parse(htmlString, parserOptions)
}

export default renderContentStackInHtml
